import React from "react";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Trash2 } from "lucide-react";

interface DrugItem {
  prescriptionItemId?: string;
  drugName?: string;
  quantity?: number | string;
  dose?: string;
  note?: string;
  [key: string]: any;
}

interface PrescriptionDetails {
  prescriptionId?: string;
  patientId?: string;
  drugs?: DrugItem[];
  [key: string]: any;
}

interface PrescriptionData {
  prescriptionDetails?: PrescriptionDetails;
  recordedAt?: string;
  [key: string]: any;
}

interface PrescriptionItem {
  id?: string;
  details?: {
    payload?: string;
    [key: string]: any;
  };
  displayTimestamp?: string;
  [key: string]: any;
}

export const PrescriptionsItems = ({
  items,
  isDarkMode,
  onDelete,
  handlePrescriptionClick,
}: any) => {
  // Sort items by timestamp (oldest first)
  const sortedItems = [...items].sort(
    (a: PrescriptionItem, b: PrescriptionItem) => {
      const dateA = new Date(a.displayTimestamp || 0).getTime();
      const dateB = new Date(b.displayTimestamp || 0).getTime();
      return dateA - dateB;
    },
  );

  return (
    <div className="px-3 pb-3 mt-3 space-y-2">
      {sortedItems.map((action: PrescriptionItem, index: number) => {
        // Parse the payload to get prescription data
        let prescriptionData: PrescriptionData = {};

        try {
          if (
            action.details?.payload &&
            typeof action.details.payload === "string"
          ) {
            prescriptionData = JSON.parse(
              action.details.payload,
            ) as PrescriptionData;
          } else {
            prescriptionData = action.details || {};
          }
        } catch (e) {
          console.warn("Failed to parse prescription payload", e);
          prescriptionData = action.details || {};
        }

        const prescriptionDetails = prescriptionData.prescriptionDetails || {};
        const drugs = prescriptionDetails.drugs || [];
        const recordedAt =
          prescriptionData.recordedAt || action.displayTimestamp;

        return (
          <div
            key={action.id || index}
            className="flex items-start gap-2 p-2 group"
            onClick={() => handlePrescriptionClick(prescriptionData)}
          >
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-xs font-mono text-gray-600`}>
                  #{index + 1}
                </span>
                <span
                  className={`text-sm font-semibold ${
                    isDarkMode ? "text-gray-200" : "text-gray-900"
                  }`}
                >
                  Prescription{" "}
                  {drugs.length > 0 ? `(${drugs.length} items)` : ""}
                </span>
                {recordedAt && (
                  <span
                    className={`text-xs ml-auto ${
                      isDarkMode ? "text-gray-500" : "text-gray-400"
                    }`}
                  >
                    {new Date(recordedAt).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                )}
              </div>

              <div className="ml-6 space-y-2">
                {drugs.map((drug: DrugItem, drugIndex: number) => (
                  <div
                    key={drug.prescriptionItemId || drugIndex}
                    className="space-y-1"
                  >
                    <div
                      className={`text-xs ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      <span className="font-medium">{drug.drugName}</span>
                    </div>
                    <div
                      className={`text-xs ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      <span className="font-medium">Dosage:</span>{" "}
                      {drug.dose || "Not specified"}
                      <span className="mx-1">•</span>
                      <span className="font-medium">Qty:</span>{" "}
                      {drug.quantity || "Not specified"}
                    </div>
                    {drug.note && (
                      <p
                        className={`text-xs italic ${
                          isDarkMode ? "text-gray-500" : "text-gray-500"
                        }`}
                      >
                        {drug.note}
                      </p>
                    )}
                    {drugIndex < drugs.length - 1 && (
                      <div
                        className={`border-t my-2 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Delete button with AlertDialog */}
            <div
              className="flex items-center gap-1 transition-opacity duration-200 opacity-0 group-hover:opacity-100"
              onClick={(e) => e.stopPropagation()}
            >
              <div onClick={(e) => e.stopPropagation()}>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    {/* <button
                      className={`p-1 rounded transition-colors ${
                        isDarkMode
                          ? "hover:bg-red-900/20 text-red-400 hover:text-red-300"
                          : "hover:bg-red-50 text-red-600 hover:text-red-700"
                      }`}
                      title="Delete prescription"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button> */}
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Prescription</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete this prescription? This
                        cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => onDelete?.(action.id)}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
